const currencyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Format currency; negative values use red styling class when requested. */
export function formatAllocationAmount(
  value: number,
): { text: string; className: string } {
  const isNegative = value < -0.005
  return {
    text: currencyFmt.format(value),
    className: isNegative ? 'text-red-600' : '',
  }
}

export function allocationAmountClassName(value: number): string {
  return value < -0.005 ? 'text-red-600' : ''
}

/** Parse net income input; allows negatives (losses). Returns null when invalid. */
export function parseNetIncomeAmount(raw: string): number | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const normalized = trimmed.replace(/[$,\s]/g, '')
  if (!/^-?\d+(\.\d{0,2})?$/.test(normalized)) return null
  const parsed = Number(normalized)
  if (!Number.isFinite(parsed)) return null
  return parsed
}

/** Format net income for inputs ($#,##0.00), including negatives. */
export function formatNetIncomeField(value: number): string {
  if (value === 0) return ''
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

const toIsoDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export function lastFullCalendarMonth(asOf: Date = new Date()): {
  periodStart: string
  periodEnd: string
} {
  const year = asOf.getMonth() === 0 ? asOf.getFullYear() - 1 : asOf.getFullYear()
  const month = asOf.getMonth() === 0 ? 11 : asOf.getMonth() - 1
  const start = new Date(year, month, 1)
  const end = new Date(year, month + 1, 0)
  return { periodStart: toIsoDate(start), periodEnd: toIsoDate(end) }
}

/** YYYY-MM for the most recently completed calendar month. */
export function lastFullCalendarMonthId(asOf: Date = new Date()): string {
  return lastFullCalendarMonth(asOf).periodEnd.slice(0, 7)
}

/** Every YYYY-MM from startYm through the last full calendar month, newest first. */
export function reportMonthsRange(startYm: string, asOf: Date = new Date()): string[] {
  const endYm = lastFullCalendarMonthId(asOf)
  if (endYm < startYm) return []
  const months: string[] = []
  let year = Number(startYm.slice(0, 4))
  let month = Number(startYm.slice(5, 7))

  while (true) {
    const ym = `${year}-${String(month).padStart(2, '0')}`
    months.push(ym)
    if (ym === endYm) break
    month += 1
    if (month > 12) {
      month = 1
      year += 1
    }
  }

  return months.reverse()
}

/** Every YYYY-MM from 2025-01 through the last full calendar month, newest first. */
export function incomeAllocationMonthsRange(asOf: Date = new Date()): string[] {
  return reportMonthsRange('2025-01', asOf)
}

export function periodBoundsForMonth(ym: string): { periodStart: string; periodEnd: string } {
  const [yearStr, monthStr] = ym.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  const start = new Date(year, month - 1, 1)
  const end = new Date(year, month, 0)
  return { periodStart: toIsoDate(start), periodEnd: toIsoDate(end) }
}

export type MonthSelectOption = {
  id: string
  label: string
  status?: string
  hint?: string
}

/** Count month-selector options whose status is not complete. */
export function countNonCompleteMonthOptions(
  options: { status?: string }[],
): number {
  return options.filter((option) => option.status !== 'complete').length
}

/** Oldest non-complete month; if all complete, return newest (first option). */
export function pickOldestNonCompleteMonth(options: MonthSelectOption[]): string | null {
  if (options.length === 0) return null

  for (let i = options.length - 1; i >= 0; i -= 1) {
    const option = options[i]
    if (option && option.status !== 'complete') {
      return option.id
    }
  }

  return options[0]?.id ?? null
}
